import { Dimensions, IAuditedEntity, IsoDateStr, Quantity } from '@elderbyte/ngx-starter'
import { Bom, BomComponent, PropertySet, PropertySetValue } from './bom/bom'
import { MaterialComponent } from './bom/material-component'
import { ProcessingType } from '../../process/processing-type/processing-type'
import { ProcessLeadTimeSpec } from '../../process/process-plans/process-plan'
import { AuditedProcess } from '../../audited-process'
import { ExternalPackageState, PackageLocation, PackageState } from '../../packages/package'
import { WorkBatchLine } from '../batches/work-batch'

export class WorkItemEvent {
  constructor(
    public itemId: string,
    public item: WorkItem
  ) {}
}

export enum WorkItemState {
  /**
   * This work item can be produced (released to the shopfloor).
   * releaseAt date must be respected, however.
   */
  OPEN = 'OPEN',

  /**
   * Production route of item is definitive.
   * Preparing work is done at work station.
   * Work station not running yet.
   */
  PRE_PROCESS = 'PRE_PROCESS',

  /**
   * One or more segments of this work item is being produced.
   */
  PROCESSING = 'PROCESSING',

  /**
   * The production of this work item has finished.
   */
  COMPLETED = 'COMPLETED',
}

export enum WorkItemSegmentState {
  OPEN = 'OPEN',

  /**
   * The segment processing is confirmed
   */
  PROCESSING = 'PROCESSING',

  /**
   * The execution is completed
   */
  COMPLETED = 'COMPLETED',
}

export class WorkItemSegmentStateUtil {
  public static getAllNonCompleted(): WorkItemSegmentState[] {
    return [WorkItemSegmentState.OPEN, WorkItemSegmentState.PROCESSING]
  }
}

export enum WorkTransferState {
  /**
   * No data has yet been sent to the gateway
   */
  OPEN = 'OPEN',

  /**
   * Currently sending the data to gateway
   */
  SENDING = 'SENDING',

  /**
   * The data has arrived at the destination
   */
  CONFIRMED = 'CONFIRMED',

  /**
   * The data has not arrived (completely) on the destination
   */
  FAILED = 'FAILED',
}

export interface ErrorData {
  title?: string
  detail?: string
  stacktrace?: string
}

export class WorkItemSegment {
  public workCenterId: string
  public workCenterNumber: string
  public workCenterName?: string
  public capability: string
  public actions: string[] = []
  public state: WorkItemSegmentState

  public processingAudit: AuditedProcess

  public validationState: ValidationState

  public plannedCapacityUsage?: string // Duration
  public plannedMakeSpan?: string // Duration
  public plannedTransportCost?: string // Duration

  public consumptionBom?: Bom

  public finishedBom?: Bom

  public productModel?: BomComponent

  public processingSequencePos?: number

  public properties: PropertySet

  public sequenceNumber?: string
}

export enum ValidationState {
  /**
   * Validation has not yet been performed.
   */
  PENDING = 'PENDING',

  /**
   * Validation successful.
   */
  VALIDATED = 'VALIDATED',

  /**
   * Validation unsuccessful.
   */
  INVALID = 'INVALID',
}

export class ItemProductionRoute {
  public productionPath: string
  public sequence: WorkItemSegment[] = []
  public makeSpan: string // Duration
}

export class WorkBatchRef {
  constructor(
    public id: string,
    public number: string
  ) {}
}

export class WorkItemUpdateOne {
  public id: string

  public processPlanId?: string
  public optimizeAssignment?: boolean
}

export enum WorkItemPackagingState {
  OPEN = 'OPEN',
  PARTIAL = 'PARTIAL',
  COMPLETE = 'COMPLETE',
}

export class WorkItem implements IAuditedEntity {
  public id: string
  public company: string

  public processingType: ProcessingType

  public manufactureFacility: string

  public collectingFacility: string

  public prodOrderId: string

  public leadTimeSpec: ProcessLeadTimeSpec

  public scheduleValidation: ValidationState

  public processValidation: ValidationState

  public workOrderId: string

  public number: string

  public processingState: WorkItemState

  public packagingState: WorkItemPackagingState

  // Nullable fields

  public description: string

  public note: string

  public totalWeight: Quantity

  public dimensions: Dimensions

  public labels: string[]

  public orderedProduct: MaterialComponent

  public producedProduct: MaterialComponent

  public imageUrl: string = null

  public processPlanId: string

  public productionRoute: ItemProductionRoute

  public workBatch: WorkBatchRef | null

  public optimizeAssignment: boolean

  public packages: UnitBundleRef[] // TODO Should be called bundles

  /**
   * Holds the design and functionality of the order item
   */
  public engineeringBom: Bom = null

  /**
   * Holds detail structure, assembly and sub designs
   */
  public manufacturingBom: Bom = null

  /**
   * Holds the consumed materials of this item
   */
  public consumptionBom: Bom = null

  /**
   * Holds the detail structure's final state, as well as incidental scrap
   */
  public finishedBom: Bom = null

  public plannedSegments: PlannedSegment[]

  createdAt: string
  createdBy: string
  modifiedAt: string
  modifiedBy: string
}

export class PackageDetail {
  public state: PackageState
  public dispatchState: ExternalPackageState
  public dimensions?: Dimensions
  public longestItemDimensions?: Dimensions
  public location?: PackageLocation
  public isUsedShared: boolean
}

export class UnitBundleRef {
  public id: string
  public packageId: string
  public packageNumber: string
  public quantity?: Quantity
  public weight?: Quantity
  public detail?: PackageDetail
}

export interface PlannedSegment {
  id: string
  segmentActions: string[]
  enrichments?: Enrichments
}

export interface Enrichments {
  propertyById: Map<string, EnrichmentProperty>
  requestedAt?: IsoDateStr
  error?: string
}

export interface EnrichmentProperty {
  propertyId: string
  propertyValues: PropertySetValue[]
  error?: string
  enrichedAt: IsoDateStr
}

export class WorkItemUtils {
  public static segmentsAreValid(item: WorkItem): boolean {
    const states = item?.productionRoute?.sequence?.map((s) => s.validationState)

    if (!states || states.includes(ValidationState.INVALID)) {
      return false
    } else if (states.includes(ValidationState.PENDING)) {
      return false
    } else {
      return true
    }
  }

  public static bomsAreValid(item: WorkItem): boolean {
    return (
      this.bomIsValid(item?.consumptionBom, item?.processingType) &&
      this.bomIsValid(item?.finishedBom, item?.processingType)
    )
  }

  public static bomIsValid(bom: Bom, processingType: ProcessingType): boolean {
    switch (processingType) {
      case ProcessingType.PRODUCED:
        return !!bom
      case ProcessingType.PICKING:
        return !bom
      case ProcessingType.UNKNOWN:
        return false
    }
  }

  public static isCompletelyPackaged(item: WorkItem): boolean {
    return item?.packagingState === WorkItemPackagingState.COMPLETE
  }

  public static calcPackagedQuantity(item: WorkItem): number {
    return item.packages.reduce((sum, current) => sum + current.quantity.value, 0)
  }

  public static calcUnPackagedQuantity(item: WorkItem): number {
    return item.producedProduct.quantity - this.calcPackagedQuantity(item)
  }

  public static getSortByProcessingSequencePosString(
    line: WorkBatchLine,
    workCenterId: string
  ): string {
    const posInLine = WorkBatchLine.getPosInSequence(line, workCenterId)
    return 'productionRoute.sequence.' + posInLine + '.processingSequencePos'
  }

  public static getProcessingSequencePos(item: WorkItem, workCenterId: string): number {
    return item?.productionRoute?.sequence?.find((segment) => segment.workCenterId === workCenterId)
      .processingSequencePos
  }
}
